import { useCallback, useRef, useState } from 'react';
import api from '@helpers/api';
import {
    DEFAULT_ACTIONS_BY_PLATFORM,
    newActionKey,
} from '../enums/actionTypes';

const EMPTY_INTEGRATION = {
    id: null,
    plan_id: '',
    event: 'payments.confirmed',
    platform: '',
    name: '',
    active: false,
};

function buildActionsFromRaw(rawActions = []) {
    return rawActions.map(a => ({
        _key: newActionKey(),
        id: a.id,
        type: a.type || a.action_type,
        config: a.config || a.action_config || {},
    }));
}

function buildDefaultActions(platform) {
    return (DEFAULT_ACTIONS_BY_PLATFORM[platform] ?? []).map(a => ({
        _key: newActionKey(),
        id: null,
        type: a.type,
        config: { ...a.config },
    }));
}

export default function useManageIntegration({ communityRef }) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('create'); // 'create' | 'edit'
    const [step, setStep] = useState(0); // 0 = integration form, 1 = actions (create only)
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [integration, setIntegration] = useState({ ...EMPTY_INTEGRATION });
    const [actions, setActions] = useState([]);
    const [removedActionIds, setRemovedActionIds] = useState([]);

    // Integration created in step 1 (create flow) – need to track for cancel/rollback
    const createdIntegrationId = useRef(null);
    const callbackRef = useRef(() => { });

    // ─── Expose imperative API ───────────────────────────────────────────────

    const openCreate = useCallback((callback = () => { }) => {
        callbackRef.current = callback;
        createdIntegrationId.current = null;
        setIntegration({ ...EMPTY_INTEGRATION });
        setActions([]);
        setRemovedActionIds([]);
        setStep(0);
        setError(null);
        setMode('create');
        setOpen(true);
    }, []);

    const openEdit = useCallback(async (integrationSummary, callback = () => { }) => {
        callbackRef.current = callback;
        createdIntegrationId.current = null;
        setError(null);
        setMode('edit');
        setStep(0);
        setLoading(true);
        setOpen(true);

        try {
            const res = await api.get(`integration/${integrationSummary.id}`);
            const data = res?.data ?? res;
            setIntegration({
                id: data.id,
                plan_id: data.plan ?? data.plan_id ?? '',
                event: data.event ?? '',
                platform: data.platform ?? '',
                name: data.name ?? '',
                active: data.active ?? true,
            });
            setActions(buildActionsFromRaw(data.actions ?? []));
            setRemovedActionIds([]);
        } catch (e) {
            console.error(e);
            setError('Não foi possível carregar a integração.');
        } finally {
            setLoading(false);
        }
    }, []);

    // ─── Field helpers ───────────────────────────────────────────────────────

    const updateIntegration = useCallback((patch) => {
        setIntegration(prev => ({ ...prev, ...patch }));
    }, []);

    // ─── Validation ──────────────────────────────────────────────────────────

    function validateStep0() {
        if (!integration.platform) return 'Selecione a plataforma.';
        if (!integration.plan_id) return 'Selecione um plano.';
        if (!integration.event) return 'Selecione o evento.';
        if (!integration.name || integration.name.trim().length < 3) return 'O nome deve ter ao menos 3 caracteres.';
        if (integration.name.trim().length > 50) return 'O nome pode ter no máximo 50 caracteres.';
        return null;
    }

    function validateActions() {
        for (let i = 0; i < actions.length; i++) {
            const a = actions[i];
            if (a.type === 'webhook_call' && !a.config?.webhook_url?.trim()) {
                return `A URL do webhook na ação ${i + 1} é obrigatória.`;
            }
        }
        if (actions.length === 0) return 'Adicione ao menos uma ação.';
        return null;
    }

    // ─── Create flow ─────────────────────────────────────────────────────────

    async function handleCreateStep0() {
        const err = validateStep0();
        if (err) { setError(err); return; }

        setError(null);
        setSubmitting(true);
        try {
            const community = communityRef.current;
            const payload = {
                community: community?.id,
                plan: integration.plan_id || null,
                event: integration.event,
                platform: integration.platform,
                name: integration.name.trim(),
                active: false, // will be activated after actions are saved
            };
            const res = await api.post('integration', payload);
            const data = res?.data ?? res;
            if (res?.response?.status >= 400 || !data?.id) {
                setError(data?.message || 'Erro ao criar integração.');
                return;
            }
            createdIntegrationId.current = data.id;
            setIntegration(prev => ({ ...prev, id: data.id }));

            // Pre-populate default actions for the chosen platform
            setActions(buildDefaultActions(integration.platform));
            setStep(1);
        } catch (e) {
            console.error(e);
            setError('Erro ao criar integração. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    }

    async function handleCreateStep1() {
        const err = validateActions();
        if (err) { setError(err); return; }

        setError(null);
        setSubmitting(true);
        try {
            const integrationId = createdIntegrationId.current;
            for (let i = 0; i < actions.length; i++) {
                const a = actions[i];
                await api.post(`integration/${integrationId}/actions`, {
                    type: a.type,
                    config: a.config,
                    priority: i + 1,
                });
            }
            window.setSnackbar('Integração criada com sucesso');
            setOpen(false);
            callbackRef.current();
        } catch (e) {
            console.error(e);
            setError('Erro ao salvar as ações. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    }

    // ─── Edit flow ───────────────────────────────────────────────────────────

    async function handleEdit() {
        const err0 = validateStep0();
        if (err0) { setError(err0); return; }
        const errA = validateActions();
        if (errA) { setError(errA); return; }

        setError(null);
        setSubmitting(true);
        try {
            const integrationId = integration.id;

            // 1. Update integration name + active
            await api.put(`integration/${integrationId}`, {
                name: integration.name.trim(),
                active: integration.active,
            });

            // 2. Delete removed actions
            for (const actionId of removedActionIds) {
                await api.delete(`integration/${integrationId}/actions/${actionId}`);
            }

            // 3. Update or create actions
            for (let i = 0; i < actions.length; i++) {
                const a = actions[i];
                if (a.id) {
                    await api.put(`integration/${integrationId}/actions/${a.id}`, {
                        type: a.type,
                        config: a.config,
                        priority: i + 1,
                    });
                } else {
                    await api.post(`integration/${integrationId}/actions`, {
                        type: a.type,
                        config: a.config,
                        priority: i + 1,
                    });
                }
            }

            window.setSnackbar('Integração atualizada com sucesso');
            setOpen(false);
            callbackRef.current();
        } catch (e) {
            console.error(e);
            setError('Erro ao salvar. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    }

    // ─── Actions management ──────────────────────────────────────────────────

    function handleAddAction() {
        const platform = integration.platform;
        const types = (DEFAULT_ACTIONS_BY_PLATFORM[platform] ?? []);
        const defaultType = types[0]?.type ?? 'webhook_call';
        setActions(prev => [
            ...prev,
            { _key: newActionKey(), id: null, type: defaultType, config: { webhook_url: '' } },
        ]);
    }

    function handleUpdateAction(key, patch) {
        setActions(prev =>
            prev.map(a => a._key === key ? { ...a, ...patch } : a)
        );
    }

    function handleRemoveAction(key) {
        setActions(prev => {
            const removed = prev.find(a => a._key === key);
            if (removed?.id) {
                setRemovedActionIds(ids => [...ids, removed.id]);
            }
            return prev.filter(a => a._key !== key);
        });
    }

    async function handleToggle() {
        if (!integration.id) return;

        const previousActive = !!integration.active;

        // Optimistically update
        setIntegration(prev => ({ ...prev, active: !previousActive }));

        try {
            const res = await api.patch(`integration/${integration.id}/toggle`);
            if (res?.response?.status >= 400) {
                throw new Error(res?.response?.data?.message || 'Erro ao alternar status.');
            }
            window.setSnackbar(`Integração ${!previousActive ? 'ativada' : 'desativada'} com sucesso`);
        } catch (e) {
            console.error(e);
            window.setSnackbar(e.message || 'Não foi possível alterar o status da integração');
            // Revert
            setIntegration(prev => ({ ...prev, active: previousActive }));
        }
    }

    // ─── Modal control ───────────────────────────────────────────────────────

    async function handleClose() {
        // If we're on step 1 of creation (integration was already created), delete it
        if (mode === 'create' && step === 1 && createdIntegrationId.current) {
            try {
                await api.delete(`integration/${createdIntegrationId.current}`);
            } catch (e) {
                console.error('Failed to rollback integration on cancel', e);
            }
            createdIntegrationId.current = null;
        }
        setOpen(false);
        setError(null);
        callbackRef.current();
    }

    function handleBack() {
        if (mode === 'create' && step === 1) {
            setStep(0);
            setError(null);
        }
    }

    async function handleSubmit() {
        if (mode === 'create') {
            if (step === 0) await handleCreateStep0();
            else await handleCreateStep1();
        } else {
            await handleEdit();
        }
    }

    function handleSecondary() {
        if (mode === 'create' && step === 1) {
            handleBack();
        } else {
            handleClose();
        }
    }

    // ─── Derived ─────────────────────────────────────────────────────────────

    const isCreate = mode === 'create';
    const isStep0 = step === 0;
    const submitLabel = isCreate
        ? (isStep0 ? 'Salvar Integração' : 'Salvar ações')
        : 'Salvar integração';
    const cancelLabel = isCreate && step === 1 ? 'Voltar' : 'Cancelar';
    const title = isCreate ? 'Nova Integração' : 'Editar Integração';

    return {
        // modal state
        open,
        loading,
        submitting,
        error,
        // data
        mode,
        step,
        integration,
        actions,
        // handlers
        openCreate,
        openEdit,
        updateIntegration,
        handleAddAction,
        handleUpdateAction,
        handleRemoveAction,
        handleToggle,
        handleSubmit,
        handleSecondary,
        handleClose,
        // ui
        submitLabel,
        cancelLabel,
        title,
        community: communityRef,
    };
}
